import { Layout } from '@app/Layout';
import { MasterScheduleList } from '@masterSchedule/MasterScheduleList';
import * as React from 'react';
import { BrowserRouter, Route, Routes as ReactRoutes } from 'react-router-dom';
import { MyDataFetcher } from '@config/MyDataFetcher';
import { MasterScheduleDetailPage } from '@masterSchedule/detail/MasterScheduleDetailPage';
import { TransmittalHistoryPage } from '@transmittal/history/TransmittalHistoryPage';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import { ProjectList } from '@project/ProjectList';
import { TransmittalList } from '@transmittal/TransmittalList';
import { NoAccessRight } from '@common/NoAccessRight';
import { DesignBriefList } from '@designBrief/DesignBriefList';
import { DesignBriefDetailPage } from '@designBrief/detail/DesignBriefDetailPage';
import { DesignModificationList } from '@designModification/DesignModificationList';
import { DesignModificationDetailPage } from '@designModification/detail/DesignModificationDetailPage';
import { PermitToLoadRemoveDetailPage } from '@permitToLoadRemove/detail/PermitToLoadRemoveDetailPage';
import { PermitToLoadRemoveList } from '@permitToLoadRemove/PermitToLoadRemoveList';
import { UserList } from '@userProfile/list/UserList';
import { EMasterSchedulePermission, ETwcTwcmApplicationPermission } from '@userProfile/permission/permission.constants';
import { RequireLogin } from '@common/RequireLogin';
import { TransmittalDetailPage } from '@transmittal/detailPage/TransmittalDetailPage';
import { Redirect } from '@core/Routes/Redirect';
import { ProjectHome } from '@app/ProjectHome';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { parse, stringify } from 'query-string';
import { ProjectRequestDetailPage } from '@projectRequest/ProjectRequestDetail';
import { ProjectRequestList } from '@projectRequest/ProjectRequestList';
import { CustomAxiosConfig } from '@common/CustomAxiosConfig';
import { ProjectTwcGeRequestDetailPage } from '@role/projectTwcGeRequest/detailPage/ProjectTwcGeRequestDetailPage';
import { EProjectTwcGeRequestApplicationType } from '@role/projectTwcGeRequest/projectTwcGeRequest.constants';
import { ProjectTwcGeRequestListPage } from '@role/projectTwcGeRequest/listPage/ProjectTwcGeRequestListPage';
import { AccountLocked } from '@common/AccountLocked';
import {
  hasPermissionInArray,
  isPermittedAsSiteOperationUser,
  isPermittedAsSystemUser,
} from '@userProfile/permission/permission.services';
import { SettingPage } from '@userProfile/SettingPage';
import { MyGemsPage } from '@myGems/MyGemsPage';
import PageNotFound from '@common/PageNotFound';
import { DocumentRegisterList } from '@drawing/DocumentRegisterList';
import { TransmittalBulkUploadPage } from '@transmittal/bulkUpload/TransmittalBulkUploadPage';
import { IRolePermissionFlattened } from '@userProfile/permission/permission.models';
import { ErrorBoundary } from 'react-error-boundary';
import { LogginErrorBoundary } from '@common/error/LogginErrorBoundary';

import { AnnouncementList } from '@announcement/AnnouncementList';
import { AdminPage } from '@admin/AdminPage';
import { AdminEmptyPage } from '@admin/AdminEmptyPage';

import { ReportList } from '@report/ReportList';
import { ReportChecklistStatistics } from '@report/ReportChecklistStatistics';
import { ReportT4MobileUsage } from '@report/ReportT4MobileUsage';
import { ProjectDetailPage } from '@project/ProjectDetailPage';
import { MasterScheduleTagManagePage } from '@masterSchedule/masterScheduleTag/MasterScheduleTagManagePage';
import { MasterScheduleBatchAddPage } from '@masterSchedule/MasterScheduleBatchAddPage';
import { ProtectedRoute } from './ProtectedRoute';
import { routes } from './routes.models';

export interface IRoutesProps {}

export const Routes: React.FC<IRoutesProps> = ({}) => {
  const checkPermissionTwcGeRequestPage = (permissions: IRolePermissionFlattened[]) => {
    return (
      isPermittedAsSystemUser(permissions, [
        ETwcTwcmApplicationPermission.EM_APPROVE,
        ETwcTwcmApplicationPermission.INITATE_TWC,
        ETwcTwcmApplicationPermission.ISSUE_TWC,
        ETwcTwcmApplicationPermission.APPROVE_TWC,
        ETwcTwcmApplicationPermission.DOWNLOAD_CV,
        ETwcTwcmApplicationPermission.REVOKE,
      ]) ||
      isPermittedAsSiteOperationUser(permissions, [
        ETwcTwcmApplicationPermission.EM_APPROVE,
        ETwcTwcmApplicationPermission.INITATE_TWC,
        ETwcTwcmApplicationPermission.ISSUE_TWC,
        ETwcTwcmApplicationPermission.APPROVE_TWC,
        ETwcTwcmApplicationPermission.DOWNLOAD_CV,
        ETwcTwcmApplicationPermission.REVOKE,
      ])
    );
  };
  return (
    <BrowserRouter>
      <QueryParamProvider
        adapter={ReactRouter6Adapter}
        options={{
          searchStringToObject: parse,
          objectToSearchString: stringify,
        }}
      >
        <ErrorBoundary FallbackComponent={LogginErrorBoundary}>
          <CustomAxiosConfig />
        </ErrorBoundary>
        <MyDataFetcher />
        <ReactRoutes>
          <Route path={routes.common.noProjectAccessRight.path} element={<NoAccessRight />} />
          <Route path={routes.common.accountLocked.path} element={<AccountLocked />} />

          <Route path={routes.home.path} element={<Layout />}>
            <Route index element={<ProtectedRoute element={<ProjectList />} />} />

            <Route
              path={routes.transmittals.path}
              element={<Redirect fromPath={routes.transmittals.path} toPath={routes.transmittals.list.path} />}
            >
              <Route path={routes.transmittals.list.path} element={<ProtectedRoute element={<TransmittalList />} />} />
              <Route
                path={routes.transmittals.designerTransmittal.path}
                element={
                  <ProtectedRoute
                    element={
                      <TransmittalList transmittalType={ETransmittalType.DESIGNER} transmittalTypeName="Designer" />
                    }
                  />
                }
              />
              <Route
                path={routes.transmittals.siteTransmittal.path}
                element={
                  <ProtectedRoute
                    element={<TransmittalList transmittalType={ETransmittalType.SITE} transmittalTypeName="Site" />}
                  />
                }
              />
              <Route
                path={routes.transmittals.checkerTransmittal.path}
                element={
                  <ProtectedRoute
                    element={<TransmittalList transmittalType={ETransmittalType.ICE} transmittalTypeName="Checker" />}
                  />
                }
              />
              <Route
                path={routes.transmittals.lirTransmittal.path}
                element={
                  <ProtectedRoute
                    element={<TransmittalList transmittalType={ETransmittalType.REVIEWER} transmittalTypeName="LIR" />}
                  />
                }
              />
              <Route
                path={routes.transmittals.item.path}
                element={<ProtectedRoute element={<TransmittalDetailPage />} />}
              />
              <Route
                path={routes.transmittals.designerTransmittal.item.path}
                element={<ProtectedRoute element={<TransmittalDetailPage />} />}
              />
              <Route
                path={routes.transmittals.siteTransmittal.item.path}
                element={<ProtectedRoute element={<TransmittalDetailPage />} />}
              />
              <Route
                path={routes.transmittals.checkerTransmittal.item.path}
                element={<ProtectedRoute element={<TransmittalDetailPage />} />}
              />
              <Route
                path={routes.transmittals.lirTransmittal.item.path}
                element={<ProtectedRoute element={<TransmittalDetailPage />} />}
              />
              <Route
                path={routes.transmittals.item.history.path}
                element={<ProtectedRoute element={<TransmittalHistoryPage />} />}
              />
              <Route
                path={routes.transmittals.designerTransmittal.history.path}
                element={<ProtectedRoute element={<TransmittalHistoryPage />} />}
              />
              <Route
                path={routes.transmittals.siteTransmittal.history.path}
                element={<ProtectedRoute element={<TransmittalHistoryPage />} />}
              />
              <Route
                path={routes.transmittals.checkerTransmittal.history.path}
                element={<ProtectedRoute element={<TransmittalHistoryPage />} />}
              />
              <Route
                path={routes.transmittals.lirTransmittal.history.path}
                element={<ProtectedRoute element={<TransmittalHistoryPage />} />}
              />
              <Route
                path={routes.transmittals.designerTransmittal.bulkUpload.path}
                element={<ProtectedRoute element={<TransmittalBulkUploadPage />} />}
              />
              <Route
                path={routes.transmittals.siteTransmittal.bulkUpload.path}
                element={<ProtectedRoute element={<TransmittalBulkUploadPage />} />}
              />
              <Route
                path={routes.transmittals.checkerTransmittal.bulkUpload.path}
                element={<ProtectedRoute element={<TransmittalBulkUploadPage />} />}
              />
              <Route
                path={routes.transmittals.lirTransmittal.bulkUpload.path}
                element={<ProtectedRoute element={<TransmittalBulkUploadPage />} />}
              />
            </Route>
            <Route path={routes.dashboard.path} element={<div>Dashboard placeholder</div>} />
            <Route path={routes.projectList.path} element={<ProtectedRoute element={<ProjectList />} />} />

            <Route path={routes.t1.path} element={<ProtectedRoute element={<MasterScheduleList />} />} />
            <Route
              path={routes.t1.manageHeaderTags.path}
              element={<ProtectedRoute element={<MasterScheduleTagManagePage />} />}
            />
            <Route
              path={routes.t1.batchAdd.path}
              element={
                <ProtectedRoute
                  element={<MasterScheduleBatchAddPage />}
                  checkPermission={(permissions) => {
                    return hasPermissionInArray(permissions, EMasterSchedulePermission.T1_BATCH_ADD);
                  }}
                />
              }
            />
            <Route path={routes.t1.create.path} element={<ProtectedRoute element={<MasterScheduleDetailPage />} />} />
            <Route path={routes.t1.view.path} element={<ProtectedRoute element={<MasterScheduleDetailPage />} />} />
            <Route path={routes.t2.path} element={<ProtectedRoute element={<DesignBriefList />} />} />
            <Route path={routes.t2.create.path} element={<ProtectedRoute element={<DesignBriefDetailPage />} />} />
            <Route path={routes.t2.view.path} element={<DesignBriefDetailPage />} />
            <Route path={routes.t3.path} element={<ProtectedRoute element={<DesignModificationList />} />} />
            <Route
              path={routes.t3.create.path}
              element={<ProtectedRoute element={<DesignModificationDetailPage />} />}
            />
            <Route path={routes.t3.view.path} element={<ProtectedRoute element={<DesignModificationDetailPage />} />} />
            <Route path={routes.t4.path} element={<ProtectedRoute element={<PermitToLoadRemoveList />} />} />
            <Route
              path={routes.t4.create.path}
              element={<ProtectedRoute element={<PermitToLoadRemoveDetailPage />} />}
            />
            <Route path={routes.t4.view.path} element={<ProtectedRoute element={<PermitToLoadRemoveDetailPage />} />} />
            <Route path={routes.maintenance.path} element={<div>Maintenance placeholder</div>} />
            <Route path={routes.users.path} element={<ProtectedRoute element={<UserList />} />} />
            <Route
              path={routes.users.twcRequest.list.path}
              element={<ProjectTwcGeRequestListPage applicationType={EProjectTwcGeRequestApplicationType.TWC} />}
            />
            <Route
              path={routes.users.twcRequest.create.path}
              element={
                <ProtectedRoute
                  element={<ProjectTwcGeRequestDetailPage applicationType={EProjectTwcGeRequestApplicationType.TWC} />}
                  checkPermission={(permissions) => {
                    return checkPermissionTwcGeRequestPage(permissions);
                  }}
                />
              }
            />
            <Route
              path={routes.users.twcRequest.detail.path}
              element={
                <ProtectedRoute
                  element={<ProjectTwcGeRequestDetailPage />}
                  checkPermission={(permissions) => {
                    return checkPermissionTwcGeRequestPage(permissions);
                  }}
                />
              }
            />
            <Route
              path={routes.users.geRequest.list.path}
              element={<ProjectTwcGeRequestListPage applicationType={EProjectTwcGeRequestApplicationType.GE} />}
            />
            <Route
              path={routes.users.geRequest.create.path}
              element={
                <ProtectedRoute
                  element={<ProjectTwcGeRequestDetailPage applicationType={EProjectTwcGeRequestApplicationType.GE} />}
                  checkPermission={(permissions) => {
                    return checkPermissionTwcGeRequestPage(permissions);
                  }}
                />
              }
            />
            <Route
              path={routes.users.geRequest.detail.path}
              element={
                <ProtectedRoute
                  element={<ProjectTwcGeRequestDetailPage />}
                  checkPermission={(permissions) => {
                    return checkPermissionTwcGeRequestPage(permissions);
                  }}
                />
              }
            />
            <Route path={routes.userProfile.path} element={<SettingPage />} />
            <Route path={routes.projectRequest.path} element={<ProtectedRoute element={<ProjectRequestList />} />} />
            <Route
              path={routes.projectRequest.view.path}
              element={<ProtectedRoute element={<ProjectRequestDetailPage />} />}
            />
            <Route
              path={routes.projectRequest.create.path}
              element={<ProtectedRoute element={<ProjectRequestDetailPage />} />}
            />
            <Route
              path={routes.documentRegister.path}
              element={<ProtectedRoute element={<DocumentRegisterList />} />}
            />
            <Route path={routes.projectInfo.view.path} element={<ProtectedRoute element={<ProjectDetailPage />} />} />

            <Route path={routes.myGems.path} element={<ProtectedRoute element={<MyGemsPage />} />} />
            <Route path={routes.announcement.list.path} element={<AnnouncementList />} />
            <Route path={routes.admin.item.path} element={<AdminPage />} />
            <Route path={routes.admin.main.path} element={<AdminEmptyPage />} />
            <Route path={routes.report.main.path} element={<ReportList />} />
            <Route path={routes.report.checklistStat.path} element={<ReportChecklistStatistics />} />
            <Route path={routes.report.mobileUsage.path} element={<ReportT4MobileUsage />} />

            <Route path={routes.home.project.path} element={<ProtectedRoute element={<ProjectHome />} />} />
            <Route path={routes.common.noAccessRight.path} element={<NoAccessRight />} />
            <Route path={routes.common.requireLogin.path} element={<RequireLogin />} />
            <Route path={routes.common.pageNotFound.path} element={<PageNotFound />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </ReactRoutes>
      </QueryParamProvider>
    </BrowserRouter>
  );
};
